import React, { useMemo } from 'react';
import {
  Box,
  Checkbox,
  Divider,
  FormControl,
  FormLabel,
  Heading,
  SimpleGrid,
  Text,
  Textarea,
  useColorModeValue,
  VStack,
} from '@chakra-ui/react';
import {
  mergeSchemaGrazzini,
  SCHEMA_GRAZZINI_AXES,
  SCHEMA_GRAZZINI_AREAS,
  SCHEMA_GRAZZINI_SCALE_LINES,
} from '../../constants/schemaGrazzini';
import SchemaGrazziniAxisField from './SchemaGrazziniAxisField';
import SchemaGrazziniChart from './SchemaGrazziniChart';

const AREA_ORDER = ['motoria', 'relazionale', 'cognitiva'];

function buildGroupedAreas() {
  const map = new Map(AREA_ORDER.map((a) => [a, []]));
  SCHEMA_GRAZZINI_AXES.forEach((axis) => {
    const list = map.get(axis.area);
    if (list) list.push(axis);
  });
  return AREA_ORDER.map((area) => ({
    id: area,
    title: SCHEMA_GRAZZINI_AREAS[area],
    axes: map.get(area) || [],
  }));
}

const GROUPED = buildGroupedAreas();

function SchemaGrazziniEditor({ value, onChange }) {
  const model = useMemo(() => mergeSchemaGrazzini(value), [value]);
  const scaleMuted = useColorModeValue('gray.600', 'gray.400');
  const scaleBoxBg = useColorModeValue('purple.50', 'whiteAlpha.50');
  const scaleBorder = useColorModeValue('purple.200', 'whiteAlpha.300');
  const areaShellBg = useColorModeValue('gray.50', 'gray.900');
  const areaShellBorder = useColorModeValue('gray.200', 'gray.700');
  const areaHeaderBg = useColorModeValue('white', 'gray.800');
  const previewBorder = useColorModeValue('gray.200', 'gray.600');

  const patch = (partial) => {
    onChange(mergeSchemaGrazzini({ ...model, ...partial }));
  };

  const setAxisValue = (key, v) => {
    const n = Math.round(Number(v));
    const clamped = Number.isFinite(n) ? Math.max(0, Math.min(5, n)) : 0;
    patch({ values: { ...model.values, [key]: clamped } });
  };

  return (
    <VStack align="stretch" spacing={model.enabled ? 6 : 3}>
      <FormControl>
        <Checkbox
          isChecked={model.enabled}
          onChange={(e) => patch({ enabled: e.target.checked })}
          size="lg"
        >
          <Text as="span" fontWeight="semibold">
            Abilita schema Grazzini-Hoffmann e Staccioli
          </Text>
        </Checkbox>
      </FormControl>

      {model.enabled ? (
        <VStack align="stretch" spacing={6} w="100%">
          <Box
            borderWidth="1px"
            borderRadius="lg"
            px={4}
            py={3}
            bg={scaleBoxBg}
            borderColor={scaleBorder}
          >
            <Heading
              size="xs"
              textTransform="uppercase"
              letterSpacing="0.08em"
              mb={2}
            >
              Scala dei valori
            </Heading>
            <SimpleGrid
              columns={{ base: 1, sm: 2, md: 3 }}
              spacingX={4}
              spacingY={1}
            >
              {SCHEMA_GRAZZINI_SCALE_LINES.map((line) => (
                <Text
                  key={line}
                  fontSize="sm"
                  color={scaleMuted}
                  fontWeight="medium"
                >
                  {line}
                </Text>
              ))}
            </SimpleGrid>
          </Box>

          {GROUPED.map(({ id, title, axes }) => (
            <Box
              key={id}
              borderWidth="1px"
              borderRadius="lg"
              borderColor={areaShellBorder}
              bg={areaShellBg}
              overflow="hidden"
            >
              <Box
                px={4}
                py={3}
                bg={areaHeaderBg}
                borderBottomWidth="1px"
                borderColor={areaShellBorder}
              >
                <Heading size="sm">{title}</Heading>
              </Box>
              <VStack align="stretch" spacing={4} p={4}>
                {axes.map((axis) => (
                  <SchemaGrazziniAxisField
                    key={axis.key}
                    axis={axis}
                    value={model.values[axis.key] ?? 0}
                    onChange={(nv) => setAxisValue(axis.key, nv)}
                  />
                ))}
              </VStack>
            </Box>
          ))}

          <Divider />

          <FormControl>
            <FormLabel fontSize="sm">Note (opzionali)</FormLabel>
            <Textarea
              rows={3}
              value={model.notes}
              onChange={(e) => patch({ notes: e.target.value })}
              placeholder="Annotazioni sul profilo ludico-educativo…"
            />
          </FormControl>

          <Box
            borderWidth="1px"
            borderRadius="lg"
            borderColor={previewBorder}
            px={{ base: 2, md: 3 }}
            py={2}
            overflow="visible"
          >
            <Heading size="sm" mb={2} textAlign="center">
              Anteprima grafico
            </Heading>
            <SchemaGrazziniChart
              values={model.values}
              compact
              sidePanel={false}
              dimensionList={false}
            />
          </Box>
        </VStack>
      ) : (
        <Text fontSize="sm" color={scaleMuted}>
          Attiva la casella per compilare il profilo ludico-educativo.
        </Text>
      )}
    </VStack>
  );
}

export default SchemaGrazziniEditor;
